export const STATUS_TABS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "pending_mandate", label: "Pending Mandate" },
  { value: "paused", label: "Paused" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

// Fallback static lists used before /config loads or if it fails
export const DEFAULT_FREQUENCIES = [
  { value: "daily", label: "Daily" },
  { value: "alternate_day", label: "Alternate Day" },
  { value: "weekly", label: "Weekly" },
];

export const PLAN_TYPES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

export const DEFAULT_PLANS = PLAN_TYPES;

/** Map dynamic plans from /config API response */
export const mapPlans = (plans) =>
  plans && plans.length
    ? plans.map((p) => ({
        value: String(p.plan_key || "").trim().toLowerCase(),
        label: p.plan_name || p.plan_key,
        duration_days: p.duration_days,
      }))
    : DEFAULT_PLANS;

export const DEFAULT_DELIVERY_SLOTS = [
  { value: "06:00-08:00", label: "6 AM - 8 AM" },
  { value: "08:00-10:00", label: "8 AM - 10 AM" },
];

export const DEFAULT_PAYMENT_METHODS = [
  { value: "monthly_billing", label: "Monthly Billing" },
  { value: "upi_autopay", label: "UPI Autopay" },
];

export const PAUSE_REASONS = [
  { value: "out_of_station", label: "Out of Station" },
  { value: "vacation", label: "Vacation" },
  { value: "temporary_pause", label: "Temporary Pause" },
  { value: "financial_reason", label: "Financial Reason" },
  { value: "other", label: "Other" },
];

const normalizeOptionKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

export const mapFrequencies = (freqs = []) =>
  freqs.length
    ? freqs.map((frequency) => {
        const value = normalizeOptionKey(frequency.freq_key || frequency.label);
        const label =
          value === "twice_a_day"
            ? "Twice a day"
            : frequency.label || value;
        return { value, label };
      })
    : DEFAULT_FREQUENCIES;

const toClockTime = (value) => {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  return match
    ? `${match[1].padStart(2, "0")}:${match[2]}`
    : "";
};

export const mapDeliverySlots = (slots = []) => {
  if (!slots.length) return DEFAULT_DELIVERY_SLOTS;

  const uniqueSlots = new Map();
  slots.forEach((slot) => {
    const start = toClockTime(slot.start_time);
    const end = toClockTime(slot.end_time);
    const rawKey = String(slot.slot_key || "").trim();
    const value =
      start && end
        ? `${start}-${end}`
        : rawKey.match(/^\d{1,2}:\d{2}-\d{1,2}:\d{2}$/)
        ? rawKey
        : "";

    if (value && !uniqueSlots.has(value)) {
      uniqueSlots.set(value, { value, label: slot.label || value });
    }
  });

  return uniqueSlots.size ? [...uniqueSlots.values()] : DEFAULT_DELIVERY_SLOTS;
};

const quantityUnitInfo = (unit) => {
  const normalized = String(unit || "").trim().toLowerCase();
  if (["ml", "milliliter", "milliliters", "millilitre", "millilitres"].includes(normalized)) {
    return { unit: "litre", scale: 0.001 };
  }
  if (["l", "ltr", "litre", "litres", "liter", "liters"].includes(normalized)) {
    return { unit: "litre", scale: 1 };
  }
  if (["g", "gram", "grams"].includes(normalized)) return { unit: "kg", scale: 0.001 };
  if (["kg", "kilogram", "kilograms"].includes(normalized)) return { unit: "kg", scale: 1 };
  return null;
};

/** Keep only quantity options where the numeric value agrees with its label. */
export const mapQuantities = (quantities = []) => {
  const measurePattern = /(\d+(?:\.\d+)?)\s*(millilitres?|milliliters?|ml|litres?|liters?|ltr|l|kilograms?|kg|grams?|g)\b/i;

  return quantities.flatMap((quantity) => {
    const labelMeasure = String(quantity.label || "").match(measurePattern);
    const valueUnit = quantityUnitInfo(quantity.unit);
    const labelUnit = labelMeasure && quantityUnitInfo(labelMeasure[2]);
    const numericValue = Number(quantity.value);
    const labelValue = Number(labelMeasure?.[1]);

    if (
      quantity.is_custom ||
      !valueUnit ||
      !labelUnit ||
      valueUnit.unit !== labelUnit.unit ||
      !Number.isFinite(numericValue) ||
      !Number.isFinite(labelValue) ||
      Math.abs(numericValue * valueUnit.scale - labelValue * labelUnit.scale) > 0.001
    ) {
      return [];
    }

    const scaled = numericValue * valueUnit.scale;
    // Disallow fractional values (e.g. 0.5, 1.5, 2.5); quantities should be integers (1, 2, 3...)
    if (!Number.isInteger(scaled) || scaled < 1) {
      return [];
    }

    return [{
      quantity_id: quantity.qty_id,
      label: quantity.label,
      value: scaled,
      unit: valueUnit.unit,
    }];
  });
};

export const mapPaymentMethods = (methods = []) =>
  methods.length
    ? methods.map((method) => ({
        value: normalizeOptionKey(method.pm_key || method.label),
        label: method.label || method.pm_key,
        is_recommended: Boolean(method.is_recommended),
      }))
    : DEFAULT_PAYMENT_METHODS;

export const formatDate = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const todayIso = () => new Date().toISOString().slice(0, 10);

export const tomorrowIso = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
};

export const price = (value) => `Rs ${Number(value || 0).toFixed(2)}`;

export const labelFrom = (options, value) =>
  options.find((option) => option.value === value)?.label || value || "Not set";

export const formatSubscriptionFrequency = (value) => {
  const normalized = String(value || "").trim().toLowerCase().replace(/[ -]+/g, "_");
  const labels = {
    twice_a_day: "Twice a day",
    daily: "Daily",
    alternate_day: "Every other day",
    weekly: "Weekly",
  };
  return labels[normalized] || String(value || "Not set").replace(/_/g, " ");
};

export const formatSubscriptionPayment = (value) => {
  const normalized = String(value || "").trim().toLowerCase().replace(/[ -]+/g, "_");
  const labels = {
    upi_autopay: "UPI AutoPay",
    monthly_billing: "Monthly billing",
    wallet: "Wallet",
  };
  return labels[normalized] || String(value || "Not set").replace(/_/g, " ");
};

const formatClock = (hour, minute = "00") => {
  const numericHour = Number(hour);
  if (!Number.isFinite(numericHour) || numericHour < 0 || numericHour > 23) return "";
  const suffix = numericHour >= 12 ? "PM" : "AM";
  const displayHour = numericHour % 12 || 12;
  return `${displayHour}:${minute} ${suffix}`;
};

export const formatDeliverySlot = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "Not set";
  const clockRange = raw.match(/^(\d{1,2}):(\d{2})\s*[-–]\s*(\d{1,2}):(\d{2})$/);
  if (clockRange) {
    return `${formatClock(clockRange[1], clockRange[2])} – ${formatClock(clockRange[3], clockRange[4])}`;
  }
  const namedRange = raw.match(/^(morning|evening)?\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)\s*(?:to|[-–])\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
  if (namedRange) {
    const [, period, startHour, startMinute = "00", startMeridiem, endHour, endMinute = "00", endMeridiem] = namedRange;
    const prefix = period ? `${period[0].toUpperCase()}${period.slice(1).toLowerCase()} · ` : "";
    return `${prefix}${startHour}:${startMinute} ${startMeridiem.toUpperCase()} – ${endHour}:${endMinute} ${endMeridiem.toUpperCase()}`;
  }
  return raw
    .replace(/\bto\b/gi, "–")
    .replace(/\bam\b/gi, "AM")
    .replace(/\bpm\b/gi, "PM")
    .replace(/\s+/g, " ");
};

export const formatMeasure = (value, unit) => {
  if (value === null || value === undefined || value === "") return "Not set";
  const numeric = Number(value);
  const cleanNum = Number.isFinite(numeric)
    ? (Number.isInteger(numeric) ? numeric : Math.round(numeric))
    : 0;
  const amount = cleanNum.toLocaleString("en-IN");
  const normalizedUnit = String(unit || "").trim().toLowerCase();
  const displayUnit = ({ litre: "L", liter: "L", litres: "L", liters: "L", ltr: "L", l: "L", millilitre: "ml", milliliter: "ml" })[normalizedUnit] || normalizedUnit;
  return `${amount}${displayUnit ? ` ${displayUnit}` : ""}`;
};

export const getAddressId = (address) =>
  address?.address_id ?? address?.id ?? address?.customer_address_id;

export const formatVariantSize = (item = {}) => {
  const vName = String(
    item.variant_name ||
    item.product_variant?.variant_name ||
    item.variant?.variant_name ||
    item.size ||
    ""
  ).trim();

  const uName = String(
    item.unit_name ||
    item.product_variant?.unit_name ||
    item.variant?.unit_name ||
    item.quantity_unit ||
    ""
  ).trim();

  if (!vName && !uName) return "";
  if (!uName) return vName;
  if (!vName) return uName;

  if (vName.toLowerCase().includes(uName.toLowerCase())) {
    return vName;
  }
  return `${vName} ${uName}`;
};

export const formatCleanQty = (qty) => {
  const num = Number.parseFloat(qty);
  if (!Number.isFinite(num)) return qty || 1;
  return Number.isInteger(num) ? num : Math.round(num);
};
